import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'

const RosteringPayroll = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title2">Rostering, availability, and payroll</Typography>
      <Typography>
        AmbuNet supports the workforce workflow from roster planning through to
        availability checks, annual leave visibility, overtime review, pay runs,
        pending pay estimates, and staff-facing pay summaries.
      </Typography>
      <Alert severity="info" variant="outlined">
        AmbuNet records pay-run and remittance information for operational
        control. It is not a statutory payroll engine and does not calculate tax,
        National Insurance, pension deductions, or HMRC submissions.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Plan rostered work</Typography>
      <Typography>
        Managers can use <DocLink url="/global-roster" text="Global Roster" />
        to create named draft rota plans, add roster shifts, assign staff, and
        view event, PTS, frontline, and roster work in one place. Rota plans are
        planning containers rather than date-bound periods, so planners choose
        the draft plan they are working on and build shifts into it.
      </Typography>
      <Typography>
        The Selected roster control chooses the draft rota plan currently being
        viewed or edited. Draft shifts for the selected plan appear in teal with
        a pulsing highlight, while draft shifts from other plans are hidden.
        Published rota-plan shifts move into the normal shift workflow, while
        the published plan is kept for history rather than day-to-day selection.
      </Typography>
      <Alert severity="warning" variant="outlined">
        Archiving a roster is destructive: any roster that is not Locked can be
        archived — published rosters included — and archiving{' '}
        <strong>deletes the roster's unverified, unpaid shifts</strong>. Only a
        roster containing verified or paid shifts is refused outright. Check
        the plan really is finished with before archiving it.
      </Alert>
      <Typography>
        When creating a rota-plan shift, managers can repeat it on selected days
        of the week until a chosen end date. The form previews how many shifts
        will be created before saving. Existing event, PTS, frontline, or roster
        shifts can also be opened and copied into the currently selected draft
        rota plan, pre-filling the new shift form from the original shift.
      </Typography>
      <Typography>
        Event, PTS, and frontline shifts shown in Global Roster are not just
        visible — managers can edit their time, role, pay, notes, and
        bookability directly from the roster's shift drawer, and change the
        assignment on a single shift. Source buttons such as Open Event or
        Open PTS Duty still link back to the shift's original record for
        full context, but day-to-day changes no longer require leaving the
        roster.
      </Typography>
      <Typography>
        Identical vacant shifts on the same day collapse into a single card
        with a count badge, so a run of duplicate vacancies doesn't fill the
        grid. Editing a collapsed card updates every shift it represents at
        once — the drawer shows how many and disables assignment, since
        giving one person several simultaneous identical shifts doesn't make
        sense. Assign one of them, or edit just one individually, from the
        event or PTS duty instead.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Plan on-call rotas</Typography>
      <Typography>
        When creating a rota plan, managers can mark it as an{' '}
        <strong>on-call rota</strong>. Only staff in the Manager or Supervisor
        teams can be added to an on-call rota, and staff cannot book these
        shifts themselves — they are allocated directly by a manager.
      </Typography>
      <Typography>
        Because on-call cover sits alongside a person's normal duties rather
        than replacing them, shift conflicts (overlapping shifts, approved
        leave) are shown as warnings instead of being blocked. On-call shifts
        appear with a dashed border throughout Global Roster so they're easy
        to tell apart from normal rostered work.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">On-call visibility and calling</Typography>
      <Typography>
        Whoever is currently on call appears on the{' '}
        <DocLink
          docsPage
          url="/getting-started/home-screen"
          text="Home Screen"
        />
        , scoped to the base locations a user has selected. Tapping an entry
        calls them directly: a shared standby number if one has been set for
        that base location under{' '}
        <DocLink text="Base Locations" url="/base-locations" />
        , otherwise the individual's second phone number from their employee
        record.
      </Typography>
      <Alert severity="info" variant="outlined">
        Whether the On Call Now card is visible to everyone, restricted to
        Managers and Supervisors, or hidden entirely is controlled from{' '}
        <DocLink text="Configuration -> Shifts" url="/site-configuration/shifts" />.
        This only affects the home screen card — it does not change who can
        see on-call shifts in Global Roster.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Use availability checks</Typography>
      <Typography>
        Staff submit leave, sickness, and unavailable periods from{' '}
        <DocLink url="/my-availability" text="My Availability" />, and can
        record when they are available to work. Managers review requests from{' '}
        <DocLink url="/employees/availability" text="Staff Availability" />.
        Overlapping records for the same person are refused, managers cannot
        approve their own requests, and only periods that have not yet ended
        can be cancelled by staff.
      </Typography>
      <Typography>
        On the Global Roster week view (Display: Staff) availability shows as
        bands above each person's shifts — green for available windows,
        hatched for leave and pending requests — and the Available staff only
        toggle keeps just the people who have said they can work that week.
      </Typography>
      <Typography>
        Assignment checks warn managers when a member of staff has an
        overlapping shift, approved leave, role mismatch, inactive employment
        status, training issue, or DBS issue where those checks are enabled.
      </Typography>
      <Typography>
        My Availability also shows holiday balance information, including
        entitlement, accrued leave, approved annual leave, pending requests,
        remaining leave, and projected remaining leave if pending requests are
        approved.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Maintain payroll settings</Typography>
      <Typography>
        Personnel users manage employee payroll profiles from the employee
        record Payroll tab. This includes employment type, contracted hours per
        week, annual leave entitlement, leave year start month, working hours per
        day for leave calculations, and BACS details.
      </Typography>
      <Typography>
        Employment type categorises pay-run totals as PAYE-style,
        invoice/remittance, or unknown. Annual leave fields drive the holiday
        balance shown on My Availability.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Fixed shift bonuses</Typography>
      <Typography>
        On top of the hourly or fixed rate, a shift can carry{' '}
        <strong>fixed bonus sums</strong> — each a label and an amount, such
        as "Vehicle pickup £20" or "Meal allowance £10". Bonuses are added
        when creating or editing a shift (the same editor appears on event
        shifts, PTS duty shifts, and Global Roster shifts) and are included
        everywhere pay is calculated: shift pay previews, pay-run lines, My
        Pay statements and pending-pay estimates, and staff invoice
        expectations.
      </Typography>
      <Alert severity="info" variant="outlined">
        Bonuses do not change the overtime rate — overtime is still claimed
        and approved against the shift's base rate.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Paid travel time</Typography>
      <Typography>
        Where staff are paid for travelling to and from a shift at a different
        (usually lower) rate than the clinical time, add{' '}
        <strong>paid travel time</strong> to the shift instead of creating
        separate travel shifts: the number of minutes <strong>before</strong>{' '}
        and <strong>after</strong> the shift, and a <strong>travel rate</strong>{' '}
        per hour. The shift's start and end stay as the on-site window, so
        clocking in and out, lateness, and overtime are unaffected — travel
        simply adds pay on top of the shift rate and any bonuses.
      </Typography>
      <Typography>
        Set a <strong>Default travel rate</strong> under Configuration &gt;
        Shifts to pre-fill the rate; each shift can override it. Travel pay
        appears as its own column on pay-run lines and My Pay statements, and
        on staff invoice PDFs as "(incl. £X travel)".
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Review overtime</Typography>
      <Typography>
        When a shift runs late and overtime is claimed, managers review it from{' '}
        <DocLink url="/overtime-claims" text="Overtime Claims" />. Approved
        overtime is included in payroll calculations using the approved rate and
        approved minutes. Pending or rejected overtime is not included in a
        pay-run total. Reviewing and undoing overtime decisions requires the{' '}
        Personnel team.
      </Typography>
      <Typography>
        A decided claim can be undone from the claim drawer — the decision is
        cleared and the claim returns to Pending so it can be re-reviewed with
        a different rate, minutes, or outcome. The undo action is logged
        against the shift.
      </Typography>
      <Alert severity="warning" variant="outlined">
        Because approved overtime feeds pay-run totals, a decision cannot be
        undone once the shift has been marked paid or added to a pay run. Undo
        the shift's payment (or cancel the draft pay run) first, then undo the
        overtime decision.
      </Alert>
      <Typography>
        An overtime claim belongs to the person who worked late, not to the
        shift. If a shift's assignee is removed or swapped, any overtime claim
        on it is <strong>dropped and logged</strong> against the shift so it
        can't linger as a payable claim against the replacement — and if the
        claim has already been paid or sits in a pay run, the reassignment is
        refused until payroll is unwound first.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Staff expense claims</Typography>
      <Typography>
        Staff log expense claims from <DocLink url="/my-pay" text="My Pay" />{' '}
        — an amount, description, and receipt. Personnel review them from the
        Payroll page and approve or reject each claim. Approved expenses are
        added to pay runs as their own expense lines, on top of shift pay, so
        they are paid and recorded alongside wages. A pay run can even hold
        only expense lines for a worker who has no shifts in the run.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Create and pay a pay run</Typography>
      <Typography>
        Personnel users use <DocLink url="/employees/payroll" text="Payroll" />
        to search verified, unpaid shifts and create a pay run. Creating a pay
        run snapshots each line, including worker type, shift details, hours,
        base pay, fixed bonuses, paid travel time, approved overtime, approved
        expenses, and total pay. A <strong>Missing payment type</strong> fixer
        on the Payroll page lists workers with no employment type set and lets
        you assign one in place, so pay-run totals are never grouped as
        "unknown".
      </Typography>
      <Typography>
        Only verified shifts can enter a pay run, and verification locks once
        a shift is paid or in a pay run — it cannot be unverified from there.
        Undoing an individual shift's payment is likewise refused for shifts
        paid through a pay run: cancel or deal with the run instead.
      </Typography>
      <Typography>
        Draft pay runs lock their selected shifts until the run is marked paid
        or cancelled. Marking a pay run as paid updates the underlying shifts
        and makes the statement visible to staff in My Pay. If any staff
        invoice covering the run's shifts is still <strong>Submitted</strong>{' '}
        or <strong>Queried</strong>, marking the run paid is blocked until the
        invoice is approved or rejected — otherwise the invoice would be
        stranded unmatched forever.
      </Typography>
      <Typography>
        Invoice workers can upload staff pay invoices against completed unpaid
        shifts from My Pay. Personnel users can approve, query, or reject
        invoices before payroll is finalised. Workers with a VAT number on
        their payroll profile charge standard-rate VAT on top of their pay:
        their invoices carry the VAT breakdown, and when the run is paid the
        VAT actually paid is snapshotted onto the pay run so the gross amounts
        are recorded.
      </Typography>
      <Typography>
        A paid run can be exported as a <strong>CSV</strong> from its detail
        page — one row per line with hours, pay components, expenses, and the
        VAT paid to registered invoice workers — ready for your accountant or
        payroll bureau.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Staff pay summaries</Typography>
      <Typography>
        Staff use <DocLink url="/my-pay" text="My Pay" /> to see paid pay-run
        lines that belong to them. PAYE-style workers see Pay Statements.
        Invoice-style workers see Remittance Summaries.
      </Typography>
      <Typography>
        My Pay also includes Pending Pay for completed unpaid shifts. This is an
        estimate only and stays separate from paid statements. Staff log
        expense claims here too, and track whether each is pending, approved,
        or rejected.
      </Typography>
      <Typography>
        Looking for your own pay? See{' '}
        <DocLink url="/crew/your-pay" text="Your Pay" docsPage />.
      </Typography>
      <Typography>
        Invoice workers who don't want to produce their own invoice can have
        AmbuNet <strong>generate a self-billing invoice PDF</strong> from My
        Pay: pick the completed unpaid shifts (and approved overtime) it
        covers and the PDF is created with the correct lines and totals —
        including standard-rate VAT for VAT-registered workers — then
        submitted like an uploaded invoice.
      </Typography>
    </Stack>
  </Stack>
)

export default RosteringPayroll
